import { useQuery } from "@tanstack/react-query";
import { getAttendances } from "../../api/attendance";
import { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import toastifyInstance from "../../utils/toastify";

export const useAttendance = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    const [defaultDate] = useState<Date>(() =>
        searchParams.get("date") ? new Date(searchParams.get("date") as string) : new Date()
    );

    // Tính toán query từ searchParams sử dụng useMemo
    const query = useMemo(() => ({
        attendanceDate: searchParams.get("attendanceDate")
            ? new Date(searchParams.get("attendanceDate") as string)
            : defaultDate,
        isCheckedIn: searchParams.get("isCheckedIn") || "all",
    }), [searchParams, defaultDate]);

    // Nếu searchParams chưa có các tham số cần thiết thì cập nhật chúng
    useEffect(() => {
        if (!searchParams.get("attendanceDate") || !searchParams.get("isCheckedIn")) {
            setSearchParams((prev) => {
                const newParams = new URLSearchParams(prev);
                newParams.set("date", query.attendanceDate.toISOString());
                newParams.set("isCheckedIn", query.isCheckedIn);
                return newParams;
            });
        }
    }, [searchParams, query, setSearchParams]);

    // Sử dụng react-query để lấy dữ liệu
    const { data, isLoading, error } = useQuery({
        queryKey: ["attendance", query.attendanceDate.toISOString(), query.isCheckedIn],
        queryFn: async () => {
            try {
                const data = await getAttendances(query.attendanceDate, query.isCheckedIn);
                return data;
            } catch (error: any) {
                if (error.response.data.message === 'attendance.day_off') {
                    toastifyInstance.error('Ngày nghỉ lễ');
                }
                throw error;
            }
        },
        retry: false,
    });

    // Hàm cập nhật query (cho phép update từng thuộc tính một)
    const setQuery = useCallback((newQuery: Partial<typeof query>) => {
        const updatedQuery = { ...query, ...newQuery };
        const filteredQuery = Object.entries(updatedQuery)
            .filter(([_, value]) => value !== undefined && value !== null && value !== "")
            .reduce((acc, [key, value]) => ({
                ...acc,
                [key]: typeof value === "string" ? value : (value as Date).toISOString(),
            }), {} as Record<string, string>);

        // Chỉ cập nhật searchParams nếu query thực sự thay đổi
        if (JSON.stringify(filteredQuery) !== JSON.stringify(Object.fromEntries(searchParams))) {
            setSearchParams(new URLSearchParams(filteredQuery));
        }
    }, [query, searchParams, setSearchParams]);

    return {
        data,
        isLoading,
        error,
        query,
        setQuery,
    };
};